import { SiteDesignGetPayload, XProtectVersions, ServerSettingsData, StorageSetupDatabase, GenericServerSettings, HuskyServerSettings, SiteDesignSettingsData } from 'lib/types'
import { useRef, useState, useEffect } from 'react'
import { produce } from 'immer'
import Select from '../../../components/FormComponents/Select/Select'
import { SelectionTypes } from '../../../lib/enums'
import { fetchSiteDesign, saveChangesSiteDesign } from '../../../reducers/fell/siteDesign/siteDesignActions'
import { useAppDispatch } from '../../../reducers/hooks'
import { useNavigate } from 'react-router-dom'
import { getRestoreDefaultServerSettings } from 'reducers/fell/serverSettings/serverSettingsActions'

type ServerSettingsDataSettings = {
  formData: any
  isOptionsSetupLoaded
  cameraAmount
  serverSettings: ServerSettingsData
  handleXProtectPlatformChange
  handleValueChange
  handleArchivingValueChange
  handleStorageSetupValueChange
  handleSupportedCamerasValueChange
  hardwareTypeSelect: () => JSX.Element | null
  selectedHardwareType: number
  onHandleRestoreDefaultsClick: () => void
  handleModalConfirmRestoreDefaults: () => void
  handleModalCancelRestoreDefaults: () => void
  showRestoreDefaultModal
  hasManagementServerValidationError
  setHasManagementServerValidationError
}

export const useServerSettingsData = (siteDesignPayload: SiteDesignGetPayload, setModifiedServerSettings?, updatedCameraAmount?): ServerSettingsDataSettings => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const projectIdNumber = siteDesignPayload?.projectId
  const siteIdNumber = siteDesignPayload?.siteId
  const siteDesignIdNumber = siteDesignPayload?.siteDesignId
  const formData = siteDesignPayload?.formData

  let isOptionsSetupLoaded = useRef(false)
  const [showRestoreDefaultModal, setShowRestoreDefaultModal] = useState(false)
  const [serverSettings, setServerSettings] = useState(siteDesignPayload?.siteDesign?.serverSettings)
  const [hasManagementServerValidationError, setHasManagementServerValidationError] = useState(false)
  useEffect(() => {
    setServerSettings(siteDesignPayload?.siteDesign?.serverSettings)
    if (siteDesignPayload?.siteDesign?.serverSettings) {
      isOptionsSetupLoaded.current = true
      setServerSettingChanged(false)
    }
  }, [siteDesignPayload])

  const [serverSettingChanged, setServerSettingChanged] = useState(false)
  const [selectedHardwareType, setSelectedHardwareType] = useState(serverSettings?.hardwareType)
  useEffect(() => {
    setSelectedHardwareType(serverSettings?.hardwareType)
    if (serverSettingChanged) {
      setModifiedServerSettings(serverSettings)
    }
  }, [serverSettings, setModifiedServerSettings, serverSettingChanged])

  //Handles the XProtect Platform change and checks if we need to reinitialize the "add management server" property as well based on the XProtectplatform selection
  const handleXProtectPlatformChange = updatedServerSettings => {
    setServerSettings(updatedServerSettings)
    setModifiedServerSettings(updatedServerSettings)
    setServerSettingChanged(true)
  }

  //Handling common server settings properties
  const handleValueChange = (propertyPath: [string], value: boolean | string | number) => {
    updateModifiedServerSettings(propertyPath, value)
    setServerSettingChanged(true)
  }

  const handleArchivingValueChange = (propertyPath: [string], value: boolean) => {
    //updating the archiving property
    updateModifiedServerSettings(propertyPath, value)

    //in order to get the default value for the archiving or to save the existing changes or values related to the archiving
    //we will do save changes to save for the current server settings and then do a get latest
    let siteDesignSettingsData: SiteDesignSettingsData = {
      cameras: null,
      serverSettings: {
        ...serverSettings,
        genericServerSettings: {
          ...serverSettings.genericServerSettings,
          advancedSettings: {
            ...serverSettings.genericServerSettings.advancedSettings,
            archiving: value
          }
        }
      }
    }

    //Call the save changes before applying the bulk setting
    dispatch(
      saveChangesSiteDesign({
        projectId: projectIdNumber,
        siteId: siteIdNumber,
        siteDesignId: siteDesignIdNumber,
        siteDesignData: siteDesignSettingsData
      })
    )
      .then(() => {
        return dispatch(
          fetchSiteDesign({
            projectId: projectIdNumber,
            siteId: siteIdNumber,
            siteDesignId: siteDesignIdNumber
          })
        )
          .then(() => {
            //as we have saved all changes from the camera groups, we clear the modified camera group array.
            setModifiedServerSettings(null)
            setServerSettingChanged(false)
          })
          .catch(() => navigate('/error-page'))
      })
      .catch(error => {
        console.error('Error updating camera:', error)
      })
  }

  const updateModifiedServerSettings = (propertyPath, value) => {
    try {
      setServerSettings(prev => {
        if (prev) {
          return produce(prev, draft => {
            let temp = draft
            propertyPath.slice(0, -1).forEach(key => {
              temp = temp[key] // Navigate through object
            })
            temp[propertyPath[propertyPath.length - 1]] = value // Set the new value
          })
        }

        return null
      })

      setModifiedServerSettings(prev => {
        if (prev) {
          return produce(prev, draft => {
            let temp = draft
            propertyPath.slice(0, -1).forEach(key => {
              temp = temp[key] // Navigate through object
            })
            temp[propertyPath[propertyPath.length - 1]] = value // Set the new value
          })
        }

        return null
      })

      return true
    } catch (error) {
      console.error('Error - could not update the property value:', error)
    }

    return false
  }

  //Specific event to handle when the live database or archiving database properties are changed.
  const handleStorageSetupValueChange = (propertyPath: [string], propertyName: string, value: number, isGenericServerSettings: boolean) => {
    if (propertyName) {
      const storagePropertyPath = [...propertyPath]
      storagePropertyPath.push(propertyName)
      updateModifiedServerSettings(storagePropertyPath, value)

      if (propertyName === 'diskType') {
        let storageDatabase: StorageSetupDatabase
        if (storagePropertyPath.includes('liveDatabase')) {
          if (isGenericServerSettings) {
            storageDatabase = serverSettings?.genericServerSettings?.advancedSettings?.liveDatabase
          } else {
            storageDatabase = serverSettings?.huskyServerSettings?.advancedSettings?.liveDatabase
          }
        } else {
          if (isGenericServerSettings) {
            storageDatabase = serverSettings?.genericServerSettings?.advancedSettings?.archiveDatabase
          } else {
            storageDatabase = serverSettings?.huskyServerSettings?.advancedSettings?.archiveDatabase
          }
        }

        const maxMinDiskSize = setMaxMinDiskSize(value, storageDatabase)
        if (maxMinDiskSize !== storageDatabase.diskSizeMin) {
          propertyPath.push('diskSizeMin')
          updateModifiedServerSettings(propertyPath, maxMinDiskSize)
        }
      }

      setServerSettingChanged(true)
    }
  }

  const setMaxMinDiskSize = (diskTypeValue, storageDatabase) => {
    if (diskTypeValue === 3 && storageDatabase.diskSizeMin > 7) {
      return 7
    }
    if (diskTypeValue === 1 && storageDatabase.diskSizeMin > 20) {
      return 20
    }

    return storageDatabase?.diskSizeMin
  }

  //The updatedCameraAmount is handled in the SiteDesignEditorInitialization where it is updated from the camera group table.
  const cameraAmount = updatedCameraAmount

  //Update the server settings.
  const handleSupportedCamerasValueChange = updatedServerSettings => {
    setServerSettings(updatedServerSettings)
    setModifiedServerSettings(updatedServerSettings)
    setServerSettingChanged(true)
  }

  //set the hardware type for the PDF rendering
  const hardwareTypes = formData?.hardwareTypes ?? {}
  const hardwareTypeSelect = () => {
    return (
      <Select
        id="xsd__e2e-server-settings-hardware-type"
        selectionType={SelectionTypes.Number}
        className="col-3"
        defaultOption={{ label: 'Choose Hardware type', value: '' }}
        options={hardwareTypes}
        handleDataChange={handleHardwareTypeChange(['hardwareType'])}
        initialValue={selectedHardwareType.toString()}
      />
    )
  }

  const handleHardwareTypeChange = propertyPath => (value: number) => {
    setSelectedHardwareType(value)
    handleValueChange(propertyPath, value)
  }

  const onHandleRestoreDefaultsClick = () => {
    setShowRestoreDefaultModal(true)
  }

  const handleModalConfirmRestoreDefaults = async () => {
    setShowRestoreDefaultModal(false)

    try {
      const response = await dispatch(getRestoreDefaultServerSettings())
      if (response && response.payload) {
        setServerSettings(response.payload)
        setModifiedServerSettings(response.payload)
        setServerSettingChanged(true)
      }
    } catch (error) {
      console.error('Error deleting project:', error)
      navigate('/error-page')
    }
  }

  const handleModalCancelRestoreDefaults = () => {
    setShowRestoreDefaultModal(false)
  }

  return {
    formData,
    isOptionsSetupLoaded,
    cameraAmount,
    serverSettings,
    handleXProtectPlatformChange,
    handleValueChange,
    handleArchivingValueChange,
    handleStorageSetupValueChange,
    handleSupportedCamerasValueChange,
    hardwareTypeSelect,
    selectedHardwareType,
    onHandleRestoreDefaultsClick,
    handleModalConfirmRestoreDefaults,
    handleModalCancelRestoreDefaults,
    showRestoreDefaultModal,
    hasManagementServerValidationError,
    setHasManagementServerValidationError
  }
}
